/**
 * Download button for STM32 suite. Calls POST /download with mcuId + nodeIds.
 * When backend returns zip, triggers file download; otherwise shows message.
 * Shows skeleton strip when loading (Preparing…).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_BASE = '/api/open-source/stm32';

export default function DownloadButton({ mcuId, nodeIds, disabled }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleClick = () => {
    if (!mcuId || disabled) return;
    const ts = new Date().toISOString();
    console.log(`[${ts}] [DownloadButton] click`, { mcuId, nodeIds });
    setLoading(true);
    setMessage(null);
    axios
      .post(`${API_BASE}/download`, { mcuId, nodeIds }, { responseType: 'blob' })
      .then((res) => {
        const ts2 = new Date().toISOString();
        console.log(`[${ts2}] [DownloadButton] response`, res.headers['content-type']);
        if (res.data instanceof Blob && res.headers['content-type']?.includes('zip')) {
          const url = window.URL.createObjectURL(res.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = `stm32_${mcuId}.zip`;
          a.click();
          window.URL.revokeObjectURL(url);
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const j = JSON.parse(reader.result);
              setMessage(j.message || j.error || 'Download not ready yet.');
            } catch {
              setMessage('Download not ready yet.');
            }
          };
          reader.readAsText(res.data);
        }
      })
      .catch(async (err) => {
        console.error(`[${new Date().toISOString()}] [DownloadButton] error`, err);
        let msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Download failed.';
        if (err.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const j = JSON.parse(text);
            msg = j.message || j.error || msg;
          } catch (_) { /* keep msg */ }
        }
        setMessage(msg);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <div className="relative inline-block">
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled || loading}
          className="px-6 py-3 rounded-lg bg-gray-800 border border-silver-medium text-silver-light font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[220px]"
        >
          {loading
            ? t('openSource.libraries.stm32.downloading', 'Preparing…')
            : t('openSource.libraries.stm32.download', 'Download project folder')}
        </button>
        {loading && (
          <div className="absolute -bottom-1 left-0 right-0 overflow-hidden rounded-b-lg h-1 bg-gray-900">
            <div className="h-full w-1/3 animate-pulse bg-silver-medium rounded-full" />
          </div>
        )}
      </div>
      {message && !loading && <p className="mt-2 text-silver-medium text-sm">{message}</p>}
    </div>
  );
}
